import { BoxPage } from '@/components/common/BoxPage'
import {
  Box,
  Button,
  Card,
  CardContent,
  TextField,
  Typography,
  InputAdornment,
  Divider,
  IconButton
} from '@mui/material'
import { useTranslator, useNotification } from '@/hooks'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { ActivityLogTypeEnum, User, UserTypeEnum, useChangeBulkEligibilityMutation } from '@/@data'
import React, { useState, useMemo, useEffect } from 'react'
import EligibilityRateConfirmDialog from '@/pages/cms/finance/transaction/EligibilityRateConfirmDialog'
import AppLogs from '@/components/common/AppLogs'

export default function SettingEligibilityRate() {
  const { __ } = useTranslator()
  const { id } = useParams<{ id: string }>()
  const n = useNotification()
  const navigate = useNavigate()
  const location = useLocation() as any
  const [isSetting, setIsSetting] = useState(true)
  const [isOpenLogs, setIsOpenLogs] = useState(false)

  const companyId = id

  const employeeParams = useMemo(() => {
    return {
      company_id: companyId,
      user_type: UserTypeEnum.User
    }
  }, [companyId])

  const { data: employeesData } = useQuery({
    ...User.objects.paginateQueryFactory(employeeParams),
    enabled: !!companyId
  })

  const [eligibilityRate, setEligibilityRate] = useState<string>('')
  const [validationError, setValidationError] = useState<string>('')

  const bulkEligibilityMutation = useChangeBulkEligibilityMutation()
  const [openConfirm, setOpenConfirm] = useState(false)

  const handleToggleSetting = () => {
    setIsSetting(!isSetting)
  }

  // Validate on component mount
  useEffect(() => {
    if (!eligibilityRate) {
      setValidationError(
        __(
          'You need to enter the default eligibility rate; otherwise, the system will not be able to determine the salary for your new employees.'
        )
      )
    } else {
      setValidationError('')
    }
  }, [eligibilityRate, __])

  useEffect(() => {
    const rateFromState = location?.state?.eligibility_rate

    if (rateFromState !== undefined && rateFromState !== null) {
      setEligibilityRate(String(rateFromState))
      setValidationError('')
    }
    // chỉ chạy 1 lần khi component mount
  }, [])

  const handleSaveClick = async () => {
    if (!eligibilityRate || !companyId) {
      setValidationError(
        __(
          'You need to enter the default eligibility rate; otherwise, the system will not be able to determine the salary for your new employees.'
        )
      )
      n.error(__('Please enter a valid eligibility rate'))

      return
    }

    setValidationError('')
    setOpenConfirm(true)
  }

  const handleDialogApply = (option: 'new_only' | 'all_employees') => {
    const payload = {
      is_all: option === 'all_employees' ? 1 : 0,
      company_ids: [Number(companyId)],
      exclude_company_ids: [],
      eligibility_rate: parseFloat(eligibilityRate)
    }

    bulkEligibilityMutation.mutate(
      { payload },
      {
        onSuccess: () => {
          n.success(__('Eligibility rate updated successfully'))
          setOpenConfirm(false)
        },
        onError: () => {
          n.error(__('Update eligibility rate failed'))
        }
      }
    )
  }


  const handleReset = () => {
    setEligibilityRate(location?.state?.eligibility_rate)
    setValidationError(
      __(
        'You need to enter the default eligibility rate; otherwise, the system will not be able to determine the salary for your new employees.'
      )
    )
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value

    if (value === '') {
      setEligibilityRate('')
      setValidationError(
        __(
          'You need to enter the default eligibility rate; otherwise, the system will not be able to determine the salary for your new employees.'
        )
      )
    } else {
      const val = parseFloat(value)

      if (Number.isNaN(val)) {
        setEligibilityRate('')
      } else {
        const bounded = Math.max(0, Math.min(100, val))

        setEligibilityRate(bounded.toString())
        setValidationError('')
      }
    }
  }

  return (
    <BoxPage
      className='flex'
      showBackButton={true}
      title={__('Setting Eligibility Rate')}
      breadcrumbs={[
        { label: __('Finance Management'), href: '/cms/finance/transaction' },
        { label: __('Transaction Management'), href: '/cms/finance/transaction' },
        { label: __('Transaction Employer Detail'), href: `/cms/finance/transaction/${id}` },
        { label: __('Setting Eligibility Rate') }
      ]}
    >
      <Box className='flex gap-6'>
        <Box className='flex-1'>
          <Card sx={{ borderRadius: 5 }}>
            <CardContent sx={{ p: 5 }}>
              <div className='flex justify-between mb-6'>
                <div className='flex gap-4 items-center'>
                  <Typography
                    className='border p-2 text-[13px] border-[#3551F2] rounded-4xl text-[#3551F2] font-medium'
                    variant='body2'
                    sx={{ mb: 0 }}
                  >
                    {employeesData?.data?.find(i => i.company.name)?.company?.name ?? ''}
                  </Typography>

                  <Divider orientation='vertical' sx={{ borderLeftWidth: 1 }} flexItem />

                  <Typography
                    className='text-[18px] font-medium text-[#000]'
                    variant='body2'
                    color='text.secondary'
                    sx={{ mb: 0 }}
                  >
                    {__('Eligibility Rate Default')}
                  </Typography>
                </div>
                <div className='flex items-center gap-2'>
                  <IconButton
                    size='small'
                    onClick={() => {
                      setIsOpenLogs(true)
                    }}
                    sx={{
                      width: 40,
                      height: 40,
                      backgroundColor: '#f5f5f5',
                      '&:hover': {
                        backgroundColor: '#e0e0e0'
                      }
                    }}
                  >
                    <i className='tabler-history text-[#2f2b3de6]' style={{ fontSize: 16 }} />
                  </IconButton>
                  <IconButton
                    size='small'
                    sx={{
                      width: 40,
                      height: 40,
                      backgroundColor: '#f5f5f5',
                      '&:hover': {
                        backgroundColor: '#e0e0e0'
                      }
                    }}
                    onClick={() => {
                      navigate(`/cms/finance/transaction/${companyId}`)
                    }}
                  >
                    <i className='tabler-x text-[#2f2b3de6]' style={{ fontSize: 16 }} />
                  </IconButton>
                  <IconButton
                    size='small'
                    sx={{
                      width: 40,
                      height: 40,
                      backgroundColor: '#f5f5f5',
                      '&:hover': {
                        backgroundColor: '#e0e0e0'
                      }
                    }}
                    onClick={() => {
                      handleToggleSetting()
                    }}
                  >
                    <i className='tabler-viewport-wide text-[#2f2b3de6]' style={{ fontSize: 16 }} />
                  </IconButton>
                </div>
              </div>

              {/* Description */}
              <Typography variant='body2' color='text.secondary' sx={{ mb: 4 }}>
                {__(
                  'Here you can set the default eligibility rate for your employer, which will be applied to all your new employees.'
                )}
              </Typography>

              {/* Form Section */}
              <Box>
                <Typography variant='h6' sx={{ mb: 2, fontWeight: 600 }}>
                  {__('Setting Eligibility Rate')}
                </Typography>

                <Box className='w-full mt-6'>
                  <Typography className='mb-1 font-medium'>{__('Eligibility Rate')}</Typography>
                  <TextField
                    fullWidth
                    type='number'
                    size='small'
                    value={eligibilityRate}
                    onChange={handleInputChange}
                    placeholder={__('Enter Rate Eligibility Rate Default')}
                    error={!!validationError}
                    helperText={validationError}
                    slotProps={{
                      input: {
                        endAdornment: (
                          <InputAdornment position='end'>
                            <div>%</div>
                          </InputAdornment>
                        )
                      }
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 6
                      }
                    }}
                  />
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Box>

        {/* Floating Action Panel */}
        {isSetting && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              width: 266,
              height: 140,
              bgcolor: '#fff',
              borderRadius: 5,
              p: 3,
              boxShadow: '0px 8px 24px rgba(47, 43, 61, 0.12)',
              border: '1px solid rgba(47, 43, 61, 0.08)'
            }}
          >
            <div className='w-full p-4'>
              <Button
                fullWidth
                variant='text'
                onClick={handleReset}
                sx={{
                  mb: 4,
                  textTransform: 'none',
                  fontWeight: 500,
                  py: 1.5,
                  background: '#6F83F629',
                  color: '#6F83F6'
                }}
              >
                {__('Reset')}
              </Button>
              <Button
                fullWidth
                variant='text'
                onClick={handleSaveClick}
                disabled={bulkEligibilityMutation.isPending}
                sx={{
                  textTransform: 'none',
                  fontWeight: 500,
                  py: 1.5,
                  background: '#28C76F29',
                  color: '#28C76F'
                }}
              >
                {bulkEligibilityMutation.isPending ? __('Saving...') : __('Save')}
              </Button>
            </div>
          </Box>
        )}
      </Box>
      <EligibilityRateConfirmDialog
        open={openConfirm}
        onCancel={() => setOpenConfirm(false)}
        onApply={handleDialogApply}
        effectiveRate={eligibilityRate ? parseFloat(eligibilityRate) : undefined}
        selectedCompanyIds={companyId ? [Number(companyId)] : []}
      />
      <AppLogs
        open={isOpenLogs}
        setOpen={setIsOpenLogs}
        title={__('History Menu Setting Eligibility Rate')}
        object_type={ActivityLogTypeEnum.Transaction}
        objectId={companyId || ''}
      />
    </BoxPage>
  )
}
